'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  ContactAttribute,
  ContactAttributeList,
} from '@eliadmin/core/src/components/contact/contact-attribute-list';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '../ui/button';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { FiMenu } from 'react-icons/fi';
import { usePathname } from 'next/navigation';
import { PiShoppingCart } from 'react-icons/pi';
import { LuCalendarPlus } from 'react-icons/lu';
import { LiaEnvelopeOpenTextSolid } from 'react-icons/lia';
import { MdOutlineLocalPhone } from 'react-icons/md';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug ?? '#'} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left font-semibold',
              layer === 1 && 'text-sm font-medium',
              layer && layer > 1 && 'text-xs font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();
  const pathname = usePathname();

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label={t('text3241')}
          className={cn('ea-mobile-sidebar__trigger text-black', props.className?.trigger)}
        >
          <FiMenu size={24} />
        </SheetTrigger>
        <SheetContent
          side={'right'}
          className={cn(
            'flex w-[90vw] flex-col items-start justify-start gap-6 overflow-scroll p-4 pt-28',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-6 top-10',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <div className="flex flex-col items-start justify-start gap-6">
            {props.menu?.map((item, index) => {
              const Comp = item.path
                ? isRelativeHref(getSafeHref(item.path))
                  ? Link
                  : 'a'
                : 'span';
              return (
                <SheetClose key={index} asChild>
                  <Comp
                    href={item.path ? getSafeHref(item.path) : (undefined as any)}
                    className={cn('text-lg text-black', 'font-heading', {
                      'text-primary': pathname === item.path,
                    })}
                    prefetch={false}
                  >
                    {item.name}
                  </Comp>
                </SheetClose>
              );
            })}
          </div>
          <Separator orientation="horizontal" className="bg-[#DEDEDE]" />
          <div className="flex w-full flex-col items-start justify-start gap-3">
            <SheetClose asChild>
              <Button asChild variant={'default-alt'} className="w-full">
                <Link prefetchOnHover prefetch={false} href={'/eshop'}>
                  <PiShoppingCart size={20} /> ESHOP
                </Link>
              </Button>
            </SheetClose>
            <SheetClose asChild>
              <Button asChild className="w-full">
                <Link prefetchOnHover prefetch={false} href={'/rezervacia'}>
                  <LuCalendarPlus size={20} /> REZERVÁCIA
                </Link>
              </Button>
            </SheetClose>
          </div>
          <Separator orientation="horizontal" className="bg-[#DEDEDE]" />
          <div className="flex w-full flex-col items-start justify-start gap-3">
            <p className={cn('font-heading', 'text-lg text-black')}>
              {t('text2013')}
            </p>
            <div className="flex w-full flex-col items-start justify-start gap-1">
              <Link prefetchOnHover
                prefetch={false}
                href={`mailto:${generalLayout.contact?.email}`}
                className="flex items-center justify-start gap-2 text-sm text-primary"
                target="_blank"
              >
                <LiaEnvelopeOpenTextSolid size={24} className="text-primary" />{' '}
                {generalLayout.contact?.email}
              </Link>
              <Link prefetchOnHover
                prefetch={false}
                href={`tel:${generalLayout.contact?.phone}`}
                className="flex items-center justify-start gap-2 text-sm text-primary"
                target="_blank"
              >
                <MdOutlineLocalPhone size={24} className="text-primary" />{' '}
                {generalLayout.contact?.phone}
              </Link>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
